import { BehaviorSubject } from 'rxjs';
import { CategoriesRecord, Category, SolutionGroup } from '../types/categories';
import { getCategories } from '../apis/categories';
import { sortCategoriesByName } from '../utils/categories';
import { useEffect, useState } from 'react';

interface UseCategories {
  category: Category | undefined;
  categories: Category[] | undefined;
  categoriesRecord: CategoriesRecord | undefined;
  isCategoriesLoading: boolean;
  getSolutionGroup: (slug: string) => SolutionGroup | undefined;
}

// Stores an array of top-level categories, with their subcategories hierarchically nested in the 'subCategories' field
const categories$ = new BehaviorSubject<Category[] | undefined>(undefined);

// Stores all available categories as a collection, indexed by category slug
const categoriesRecord$ = new BehaviorSubject<CategoriesRecord | undefined>(undefined);

// Indicates whether the current state is loading
const isCategoriesLoading$ = new BehaviorSubject<boolean>(false);

// This function performs the following operations:
// - Builds `categoriesRecord`, that indexes all available categories using their slugs as keys.
// - Builds `categories`, consisting of of top-level categories, with subcategories available in the `subCategories` field
// - categories and subcategories array are sorted by name
// - Returns `categories` and `categoriesRecord`
const buildCategoriesData = (categoriesData: Category[]) => {
  const categoriesRecord: CategoriesRecord = {};
  const categories: Category[] = [];

  categoriesData.forEach((category) => {
    categoriesRecord[category.slug] = category;
  });

  const addToSubcategories = (parentSlug: string, category: Category) => {
    if (categoriesRecord[parentSlug]) {
      (categoriesRecord[parentSlug].subCategories ||= [])?.push(category);
    }
  };

  categoriesData.forEach((category) => {
    if (!category.parentCategorySlug) {
      categories.push(category);
    } else {
      addToSubcategories(category.parentCategorySlug, category);
      category.higherLevelParentCategoriesSlugs?.forEach((slug) => {
        addToSubcategories(slug, category);
      });
    }
  });

  const sortCategories = (categories: Category[] | undefined) => {
    if (!categories) {
      return;
    }
    categories.sort(sortCategoriesByName);
    categories.forEach((category) => {
      sortCategories(category.subCategories);
    });
  };
  sortCategories(categories);

  return { categories, categoriesRecord };
};

const fetchCategories = async () => {
  if (isCategoriesLoading$.getValue() || categories$.getValue()) {
    return;
  }

  try {
    isCategoriesLoading$.next(true);
    const flatCategoryList = await getCategories();
    const { categories, categoriesRecord } = buildCategoriesData(flatCategoryList);
    categories$.next(categories);
    categoriesRecord$.next(categoriesRecord);
    isCategoriesLoading$.next(false);
  } catch (error) {
    isCategoriesLoading$.next(false);
  }
};

const useCategories = (slug?: string): UseCategories => {
  const [category, setCategory] = useState<Category | undefined>();
  const [categories, setCategories] = useState<Category[] | undefined>(undefined);
  const [categoriesRecord, setCategoriesRecord] = useState<CategoriesRecord | undefined>(undefined);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState<boolean>(
    isCategoriesLoading$.getValue(),
  );

  const getSolutionGroup = (slug: string): SolutionGroup | undefined => {
    const findSolutionGroup = (slug: string | undefined): SolutionGroup | undefined => {
      if (!slug || !categoriesRecord?.[slug]) {
        return undefined;
      }
      const category = categoriesRecord[slug];
      if (category.solutionGroup) {
        return category.solutionGroup;
      }
      return findSolutionGroup(category.parentCategorySlug);
    };

    return findSolutionGroup(slug);
  };

  useEffect(() => {
    const categoriesSubscription = categories$.subscribe((e) => {
      setCategories(e);
    });
    const categoriesRecordSubscription = categoriesRecord$.subscribe((e) => {
      setCategoriesRecord(e);
    });

    const isLoadingSubscription = isCategoriesLoading$.subscribe((e) => {
      setIsCategoriesLoading(e);
    });

    return () => {
      categoriesSubscription.unsubscribe();
      categoriesRecordSubscription.unsubscribe();
      isLoadingSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!categories$.getValue() || !isCategoriesLoading$.getValue()) {
      fetchCategories();
    }
  }, [slug]);

  useEffect(() => {
    setCategory(typeof slug === 'string' ? categoriesRecord?.[slug?.trim()] : undefined);
  }, [slug, categoriesRecord]);

  return {
    category,
    categories,
    categoriesRecord,
    isCategoriesLoading,
    getSolutionGroup,
  };
};

export default useCategories;
